import { useState } from "react";
import { useApp, type Match } from "../state";
import { PageHeader } from "../components/PageHeader";
import { PredictionPills } from "../components/PredictionPills";
import { TeamBadge } from "../components/TeamBadge";
import { formatLocalDay, pointsChipClass } from "../util";

export function Results() {
  const { state, me } = useApp();

  // Finished matches, reverse chronological
  const finished = state.matches
    .filter((m) => m.status === "final" || m.status === "final_pens")
    .sort((a, b) => b.kickoff_utc.localeCompare(a.kickoff_utc));

  return (
    <div className="page">
      <PageHeader
        title="Final Whistle"
        tone="green"
        sub={`${finished.length} match${finished.length === 1 ? "" : "es"} settled · Tap to inspect`}
      />

      {finished.map((m) => (
        <ResultCard key={m.id} match={m} me={me?.id} state={state} />
      ))}
    </div>
  );
}

function ResultCard({
  match, me, state,
}: {
  match: Match;
  me: string | undefined;
  state: ReturnType<typeof useApp>["state"];
}) {
  const [expanded, setExpanded] = useState(false);
  const myPick = me ? match.picks?.find((p) => p.player_id === me) : undefined;
  const myPoints = myPick?.points ?? 0;
  const isPens = match.status === "final_pens";
  const winner = match.actual?.winner;

  return (
    <div className="card result-card" onClick={() => setExpanded((e) => !e)} style={{ cursor: "pointer" }}>
      <div className="row">
        <div style={{ flex: 1 }}>
          <div className="result-scoreboard">
            <span className={`result-team ${winner === "home" ? "result-team--winner" : "result-team--loser"}`}><TeamBadge code={match.home} compact /></span>
            <strong>{match.home_score} – {match.away_score}</strong>
            <span className={`result-team ${winner === "away" ? "result-team--winner" : "result-team--loser"}`}><TeamBadge code={match.away} compact /></span>
          </div>
          <div className="when" style={{ textAlign: "left", marginTop: 2 }}>
            {formatLocalDay(match.kickoff_utc)} · {match.round}{isPens && ` · ${winner === "home" ? match.home : match.away} won on pens`}
          </div>
        </div>
        {me && (
          <span className={pointsChipClass(myPoints, match.max_points)}>{myPoints > 0 ? `+${myPoints}` : "0"}</span>
        )}
      </div>

      {expanded && match.picks && (
        <div className="result-card__details">
          <div className="section-label picks-label">PREDICTION WALL</div>
          <PredictionPills match={match} players={state.players} />
          {wasOnlyYouExact(match, me) && (
            <div className="pill" style={{ color: "var(--green)", marginTop: 8 }}>
              ONLY YOU NAILED THIS
            </div>
          )}
          {everyoneMissed(match) && (
            <div className="pill" style={{ color: "var(--ink-dim)", marginTop: 8 }}>
              EVERYONE MISSED
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function wasOnlyYouExact(m: Match, me: string | undefined): boolean {
  if (!me || !m.picks || !m.actual) return false;
  const exact = m.picks.filter(
    (p) => p.home_score === m.actual!.home && p.away_score === m.actual!.away,
  );
  return exact.length === 1 && exact[0].player_id === me;
}

function everyoneMissed(m: Match): boolean {
  if (!m.picks || m.picks.length === 0) return false;
  return m.picks.every((p) => (p.points ?? 0) === 0);
}
